// istanbul ignore file
import { useEffect, useState } from 'react';

import { useAccount } from 'reservations/account/providers/AccountProvider';
import {
  BENTO_ANALYTICS_SNOWPLOW_APP_ID,
  THEME_AGNOSTIC
} from 'reservations/analytics/constants';
import {
  StoreData,
  _onBentoAnalyticsLoad,
  _trackPageviewOnClientSideNavigation,
  addBentoAnalyticsScriptTag
} from 'reservations/analytics/helpers';
import { BentoAnalyticsExtraParams } from 'reservations/analytics/types';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';
import { getEnvironmentVariables } from 'utility/environment';
import { isServer } from 'utility/isServer';

function Analytics() {
  const [isLoaded, setIsLoaded] = useState(false);
  const {
    restaurantName: account,
    indicative_api_key: indicativeApiKey,
    snowplow_collector_url: snowplowCollectorUrl,
    bento_analytics_script_src: scriptSrc,
    dev_mode: devMode
  } = useAccount();
  const { handleOffRouteChange, handleOnRouteChange } = useCustomRouter();

  const isServerSide = isServer();
  const { ENABLE_ANALYTICS } = getEnvironmentVariables();

  useEffect(() => {
    if (ENABLE_ANALYTICS !== 'true' || isServerSide) {
      // Empty function since there is nothing to clean
      return;
    }
    const storeData = {
      account,
      devMode,
      indicativeApiKey,
      snowplowCollectorUrl,
      snowplowAppId: BENTO_ANALYTICS_SNOWPLOW_APP_ID,
      theme: THEME_AGNOSTIC
    } as StoreData;

    const script = addBentoAnalyticsScriptTag(storeData, scriptSrc, () =>
      _onBentoAnalyticsLoad(() => setIsLoaded(true))
    );

    return () => {
      document.body.removeChild(script);
    };
  }, [
    account,
    devMode,
    ENABLE_ANALYTICS,
    indicativeApiKey,
    snowplowCollectorUrl,
    isServerSide,
    scriptSrc
  ]);

  useEffect(() => {
    if (!isLoaded || isServerSide) {
      // Empty function since there is nothing to clean
      return () => {};
    }

    const trackPageviewParams = {} as BentoAnalyticsExtraParams;

    handleOnRouteChange(() =>
      _trackPageviewOnClientSideNavigation(trackPageviewParams)
    );
    return () =>
      handleOffRouteChange(() =>
        _trackPageviewOnClientSideNavigation(trackPageviewParams)
      );
  }, [isLoaded, isServerSide, handleOnRouteChange, handleOffRouteChange]);

  return null;
}

export default Analytics;
